<template>
  <div class="quote-breakdown">

    <ul v-if="orderSummaryView" class="contract-term quote-breakdown-list">
      <li>
        <span class="key">Contract Term</span>
        <span class="value">{{ this.term }} Months</span>
      </li>
      <li>
        <span class="key">Annual Mileage</span>
        <span class="value">{{ quote.definition.plan.annualDistance }} Miles</span>
      </li>
    </ul>

    <template v-if="quoteCosts">
      <div v-if="quoteCosts === 'NOT_FOUND'">
        We are unable to provide salary sacrifice costings for this vehicle using the driver details you’ve selected. Please contact the salary sacrifice team who will be able to advise the next steps.
      </div>
      <ul v-else class="quote-breakdown-list">
        <li>
                    <span class="key">
                        Gross Monthly Sacrifice:
                        <VDropdown>
                            <button class="tooltip-button"><i class="icon-info-solid"/></button>
                            <template #popper>
                                <p>The amount that your salary will be reduced by through payroll.</p>
                            </template>
                        </VDropdown>
                    </span>
          <span class="value">{{ getInCurrency(quoteCosts.gross_monthly_sacrifice) }}</span>
        </li>

        <li>
                    <span class="key red">
                        Income Tax Saved on Sacrifice
                        <VDropdown>
                            <button class="tooltip-button"><i class="icon-info-solid"/></button>
                            <template #popper>
                                <p>The estimated reduction in income tax based on the tax rate set by you in your profile.</p>
                            </template>
                        </VDropdown>
                    </span>
          <span class="value red">{{ getInCurrency(quoteCosts.income_tax_saved_on_sacrifice) }}</span>
        </li>

        <li>
                    <span class="key red">
                        National Insurance Saved on Sacrifice
                        <VDropdown>
                            <button class="tooltip-button"><i class="icon-info-solid"/></button>
                            <template #popper>
                                <p>The estimated reduction in National Insurance based on the tax rate set by you in your profile.</p>
                            </template>
                        </VDropdown>
                    </span>
          <span class="value red">{{ getInCurrency(quoteCosts.national_insurance_saved_on_sacrifice) }}</span>
        </li>

        <li>
                    <span class="key">
                        Average Benefit in Kind
                        <VDropdown>
                            <button class="tooltip-button"><i class="icon-info-solid"/></button>
                            <template #popper>
                                <p>The estimated cost of the company car tax shown as an average monthly amount, based on the tax rate set by you in your profile and the term of the agreement.</p>
                            </template>
                        </VDropdown>
                    </span>
          <span class="value">{{ getInCurrency(quoteCosts.benefit_in_kind_on_vehicle) }}</span>
        </li>

        <li class="comparison-divider"></li>

        <li class="comparison-heading">
                    <span class="key">
                        Illustrative Vehicle Monthly Net Cost
                        <VDropdown>
                            <button class="tooltip-button"><i class="icon-info-solid"/></button>
                            <template #popper>
                                <p>Shown for illustration purposes only, this is the estimated net cost of the vehicle to you based on the tax rate set in your profile.</p>
                            </template>
                        </VDropdown>
                    </span>
          <span class="value">{{ getInCurrency(quoteCosts.vehicle_net_monthly_cost) }}</span>
        </li>

        <li class="comparison-divider"></li>

        <li v-if="showFuel">
                    <span class="key">
                        Estimated fuel cost based on {{ averagePencePerMile }}p per mile
                        <VDropdown>
                            <button class="tooltip-button"><i class="icon-info-solid"/></button>
                            <template #popper>
                                <p>This is the estimated monthly cost to charge the vehicle based on the details set in the charging profile. To amend the details, please click on Set charging profile.</p>
                            </template>
                        </VDropdown>
                    </span>
          <span class="value">{{ getInCurrency(estimatedFuelCost) }}</span>
        </li>

        <li v-if="showFuel">
            <span class="key">
                Excess mileage rate (pence per mile) plus VAT at the prevailing rate
                <VDropdown>
                    <button class="tooltip-button"><i class="icon-info-solid"/></button>
                    <template #popper>
                        <p>This is the pence per mile rate that will be charged at the end of the agreement if the total mileage exceeds the contracted mileage.</p>
                    </template>
                </VDropdown>
            </span>
          <span class="value">{{ getInCurrency(quote.details.pricing.excessPPMOver / 100) }}</span>
        </li>

      </ul>
    </template>
    <template v-else>
      <div class="text-center my-4">
        <loading-icon></loading-icon>
      </div>
    </template>

  </div>

</template>

<script type="text/javascript">

import LoadingIcon from "../../loading-icon";
export default {
  name: 'salsac-quote-breakdown',
  components: {LoadingIcon},
  props: [
    'quote',
    'quoteCosts',
    'driverProfile',
    'type',
    'showFuel',
    'showTraditionalCost',
    'orderSummaryView',
    'averagePencePerMile',
    'totalEstimatedElectricCostPerMonth'
  ],
  computed: {

    q() {
      if (this.quote.quote !== undefined) {
        return this.quote.quote;
      }
      return this.quote;
    },

    term() {
      if (this.q.term !== undefined) {
        return this.q.term;
      }
      return this.q.definition.plan.term;
    },

    taxCalculations() {
      if (this.q.taxCalculations?.calculationResults) {
        return this.q.taxCalculations.calculationResults[0];
      }
      return this.q.taxCalculations;
    },

    vehicleCosts() {

      if (!this.driverProfile || !this.quote) {
        return {}
      }

      let vehicleCosts = {
        vehiclePayment: Number(this.q.details.pricing.totalIncVAT),
        insuranceProduct: Number(this.getMonthlyCost(this?.taxCalculations?.employeeCostPrivateCar?.motoringAndFinance_InsuranceCost)),
        cashAllowance: Number(this.cashAllowanceNet),
        standardGradeDriverContribution: Number(this.driverProfile.contacts[0].telephoneNumber2),
        costAsTraditionalCompanyCar: Number(this.traditionalCarCost),
        businessFuelCost: Number(this.getMonthlyCost(this?.taxCalculations?.employeeCostPrivateCar?.businessMileage_BusinessFuelCost)),
        privateFuelCost: Number(this.getMonthlyCost(this?.taxCalculations?.employeeCostPrivateCar?.privateMileage_PrivateFuelCost)),
        businessFuelReimbursement: Number(this.getMonthlyCost(this?.taxCalculations?.employeeCostPrivateCar?.businessMileage_BusinessMileageAllowance)),
        optionalFinalBalloonPayment: Number(this.q.details.pricing.balloonPaymentExVAT)
      }
      vehicleCosts.approvedMileageAllowance = Number(this.driverProfile.contacts[0].telephoneNumber1) - Math.abs(vehicleCosts.businessFuelReimbursement)
      vehicleCosts.totalMotoringExpenses = vehicleCosts.vehiclePayment + vehicleCosts.insuranceProduct
      vehicleCosts.totalMotoringAmounts = vehicleCosts.approvedMileageAllowance + vehicleCosts.cashAllowance
      vehicleCosts.netMonthlyCost = vehicleCosts.totalMotoringExpenses - Math.abs(vehicleCosts.totalMotoringAmounts)
      vehicleCosts.netFuelCost = vehicleCosts.businessFuelCost + vehicleCosts.privateFuelCost + vehicleCosts.businessFuelReimbursement
      vehicleCosts.totalNetCost = vehicleCosts.netMonthlyCost + vehicleCosts.netFuelCost


      vehicleCosts.tradeUpTradeDown = vehicleCosts.netMonthlyCost - Math.abs(vehicleCosts.standardGradeDriverContribution)

      return vehicleCosts
    },

    totalNetCost() {
      switch (this.type.toUpperCase()) {
        case 'BCH':
          return this?.taxCalculations?.employeeCostCompanyCar?.totals_NetPresentValueNPV;
        case 'PCH':
          return this?.taxCalculations?.employeeCostPrivateCar?.totals_NetPresentValueNPV;
      }
    },

    vehiclePayment() {
      const rentalsPayment = Number(this?.taxCalculations?.employeeCostPrivateCar?.motoringAndFinance_RentalsPayments);
      const maintenanceCost = Number(this?.taxCalculations?.employeeCostPrivateCar?.motoringAndFinance_MaintenanceCost);
      const residualValue = Number(this?.taxCalculations?.employeeCostPrivateCar?.motoringAndFinance_ResidualValue);
      return rentalsPayment + maintenanceCost + residualValue;
    },

    cashAllowanceNet() {
      const cashAllowance = this.getMonthlyCost(Math.abs(this?.taxCalculations?.employeeCostPrivateCar?.cashAllowanceReconciliation_CashAllowance));
      const cashIncomeTax = this.getMonthlyCost(Number(this?.taxCalculations?.employeeCostPrivateCar?.cashAllowanceReconciliation_IncomeTaxOnCashAlternative));
      const cash1NIC = this.getMonthlyCost(Number(this?.taxCalculations?.employeeCostPrivateCar?.cashAllowanceReconciliation_Class1NICOnCashAllowance));
      return cashAllowance - (cashIncomeTax + cash1NIC);
    },

    traditionalCarCost() {
      if (this.driverProfile.incomeTaxBand.ratePercent == this.q.details.biK1Rate) {
        return this.q.details.biK1;
      }

      if (this.driverProfile.incomeTaxBand.ratePercent == this.q.details.biK2Rate) {
        return this.q.details.biK2;
      }

      if (this.driverProfile.incomeTaxBand.ratePercent == this.q.details.biK3Rate) {
        return this.q.details.biK3;
      }

      if (this.driverProfile.incomeTaxBand.ratePercent == this.q.details.biK4Rate) {
        return this.q.details.biK4;
      }
    },

    estimatedFuelCost() {
      return this.totalEstimatedElectricCostPerMonth;
    }

  },
  methods: {

    getMonthlyCost(value) {
      return (Number(value) / this.term);
    },

    getInCurrency(value, currency = "£") {
      return currency + Number(value).toFixed(2);
    }

  }
}

</script>
